import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { HiUsers, HiUserAdd, HiOutlineClock, HiCheck, HiXCircle } from "react-icons/hi";
import YhTable, { YhTableColumn, YhTableCellParams } from "../../../../ui/YhTable";
import YhInput from "../../../../ui/YhInput";
import YhModal from "../../../../ui/YhModal";
import StageDetailsPanel from "../../../../ui/StageDetailsPanel";
import YhSourceIcon from "../../../../ui/YhSourceIcon";
import LeadStatCard from "../../../../ui/LeadStatCard";
import LeadFiltersDrawer, {
  countActiveLeadFilters,
  LeadFilterValues,
} from "../../../../ui/LeadFiltersDrawer";
import { YhDateRangeValue } from "../../../../ui/YhDateTimePicker";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../helpers/methods";
import { canAssignToSelf, isNiOrLostStage } from "../../../../helpers/salesLeadHelpers";
import {
  filterByViewerTeams,
  loadViewerTeamIds,
} from "../../../../helpers/assignmentScope";
import BulkAssignModal from "../../../AllLeads/components/ShowAll/BulkAssignModal";
import StageReasonModal from "../../../../global/components/StageReasonModal";
import { useLeadRealtimeRefresh } from "../../../../utils/useLeadRealtimeRefresh";
import {
  FILTER_KEYS,
  loadLeadListFilters,
  saveLeadListFilters,
  clearLeadListFilters,
  setLeadListReturnPath,
  expandStageSelection,
  parseListPagination,
  withListPagination,
  PersistedTeamLeadFilters,
} from "../../../../helpers/leadListFilters";
import {
  compareLeadsNewestFirst,
  formatLeadDateParts,
  isFreshLeadToday,
  leadArrivalTime,
  patchLeadStageOnRow,
  relativeLeadAgeLabel,
} from "../../../../helpers/leadDate";
import CustomButton from "../../../../global/components/CustomButton/CustomButton";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/close.svg";
import urls from "../../../../global/constants/UrlConstants";
import AddNewTeam from "../../../AllLeads/components/AddNewTeam/AddNewTeam";
import AddNewTeam2 from "../../../AllLeads/components/customerProfile/customerView";
import {
  assignToMembers,
  changeAt,
  changeStage,
  deleteReminder,
  getAllForms,
  getAllStage,
  getAllTeamData,
  getAllTeamMembersData,
  getDownloadLeadsExcle,
  getReminder,
  getReminderData,
  saveReminder,
} from "../../../AllLeads/components/ShowAll/ShowAllService";
import { getAllTeamLeads, getAllDepartmentsForFilter, getTeamsForFilter, getMembersForFilter } from "./ShowAllService";
import notifiers from "../../../../global/constants/NotificationConstants";
import { canExportLeadsFor } from "../../../../Shared/AppDrawer/menuPrivileges";
import CustomLoader from "../../../../global/components/CustomLoader/CustomLoader";
import CustomDialog from "../../../../global/components/CustomModal/CustomModal";
import CustomRadioButton from "../../../../global/components/CustomRadioButton/CustomRadioButton";
import { store } from "../../../../utils/store";
import { useSelector } from "react-redux";
import { selectActiveOrganizationId } from "../../../../redux/authSlice";
import { ReactComponent as VerticalDotIcon } from "../../../../assets/icons/verticalDot.svg";
import LeadDetailsComponent from "../../../../global/components/sidePanel/leadDetail";
import {
  getAllSource,
  getRemark,
  saveRemark,
} from "../../../MyLead/components/ShowAll/ShowAllService";
import { applyLicenseKey } from "../../../../utils/License";
import { getStage } from "../../../MetaData/components/MetaList/components/LeadStageData/LeadStageListService";
import { getAllHierarchyMeta } from "../../../MetaData/components/MetaList/components/CompanyHierarchyList/companyHierarchyService";

/** react-icons + React 19 type bridge */
type IconComp = React.ComponentType<{ size?: number; className?: string }>;
const UsersIcon = HiUsers as IconComp;
const UserAddIcon = HiUserAdd as IconComp;
const ClockIcon = HiOutlineClock as IconComp;
const CheckIconRi = HiCheck as IconComp;
const XCircleIcon = HiXCircle as IconComp;
const DownloadSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <path d="M7 10l5 5 5-5" />
    <path d="M12 15V3" />
  </svg>
);

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);


const CalendarSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

const EditSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const leadActivityTime = (row: any) => leadArrivalTime(row);
const relativeLeadTag = (dateTime: any): string | null =>
  relativeLeadAgeLabel(dateTime);
const sortLeadsByAssigned = compareLeadsNewestFirst;

const ShowAll = () => {
  const activeOrganizationId = useSelector(selectActiveOrganizationId) || "";
  interface Lead {
    [key: string]: string | number | null | undefined;
  }
  const history = useHistory();
  const location = useLocation<any>();
  const privileges = store.getState().auth?.privileges || {};
  const canExportLeads = canExportLeadsFor(privileges, "Team Leads");
  const persisted =
    loadLeadListFilters<PersistedTeamLeadFilters>(
      FILTER_KEYS.teamLeads,
      String(store.getState().auth?.userId || "")
    ) || {};
  const [attentionFilter, setAttentionFilter] = useState(
    () => Boolean(persisted.attentionFilter)
  );
  const [untouchedDays, setUntouchedDays] = useState(
    () => Number(persisted.untouchedDays) || 3
  );
  const [freshFilter, setFreshFilter] = useState(
    () => Boolean(persisted.freshFilter)
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [stageReason, setStageReason] = useState<{
    open: boolean;
    stage: string;
    lead: any;
  }>({ open: false, stage: "", lead: null });
  const [savingStageReason, setSavingStageReason] = useState(false);
  const [userDataSource, setUserDataSource] = useState<any>([]);
  const [userDataSource1, setUserDataSource1] = useState<any>([]);
  const [addNewTeamDialog, setNewTeamDialog] = useState<any>({
    open: false,
    edit: false,
  });
  const [customerDetails, setCustomerDetails] = useState<any>({
    open: false,
    edit: false,
  });

  const [eventForEdit, setEventForEdit] = React.useState();
  const [userId, setUserId] = useState("");
  const [apiResponse, setApiResponse] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setdownloadLoading] = useState(false);
  const [response, setResponse] = useState<any>();
  const [selectedUser, setSelectedUser] = useState({ id: "", status: 0 });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [userRow_Id, setUserRow_Id] = useState<string>();
  const [rowStatus, setRowStatus] = useState();
  const [showSideBar, setShowSideBar] = useState(false);
  const [dynamicGridValue, setDynamicGridValue] = useState<any>();
  const [addinationalValue, setaddinationalValue] = useState<any>();
  const [leadOption, setLeadOption] = useState<any>([]);
  const [leadOptionMembers, setLeadOptionMembersas] = useState<any>([]);
  const [reminderStatus, setReminderStatus] = useState<any>("");
  const currentDateTime = new Date().toISOString().slice(0, 16);
  const [reminderData, setReminderData] = useState<any>({
    date: currentDateTime,
  });
  const [responseValuereminder, setResponseValuereminder] = useState<any>([]);
  const [formName, setFormName] = useState<any>([]);
  const [tableData, setTableData] = useState<any>([]);
  const [stageData, setStageData] = useState<any>([]);
  const [selectedOptions, setSelectedOptions] = useState<any>([]);
  const [selectedOptionsMembers, setSelectedOptionsMembers] = useState<any>([]);
  const [responseValueremark, setResponseValueremark] = useState<any>([]);
  const [idForTeam, setIdForTeam] = useState<any>([]);
  const [idForMember, setIdForMember] = useState<any>([]);
  const [leadData, setLeadData] = useState<any>();
  const [openModal, setOpenModal] = useState<any>({
    name: "",
    data: {},
    open: false,
  });
  const [paginationModel, setPaginationModel] = React.useState(() =>
    parseListPagination(location.search, {
      page: persisted.page || 0,
      pageSize: persisted.pageSize || 25,
    })
  );
  const [metaData, setMetaData] = useState<any>({});
  const [stageSourse, setStageSourse] = useState<any>([]);
  const [remData, setRemData] = useState<any>([]);
  const [search, setSearch] = useState(() => persisted.search || "");
  const [searchInput, setSearchInput] = useState(
    () => persisted.searchInput || persisted.search || ""
  );
  const [selectedStages, setSelectedStages] = useState<string[]>(() => {
    if (Array.isArray(persisted.selectedStages) && persisted.selectedStages.length) {
      return persisted.selectedStages.map(String).filter(Boolean);
    }
    // Migrate legacy single stage filter
    const legacy = persisted.stage;
    if (legacy && typeof legacy === "object" && legacy.label) {
      return [String(legacy.label)];
    }
    if (typeof legacy === "string" && legacy.trim()) {
      return [legacy.trim()];
    }
    return [];
  });
  const [source, setSource] = useState<any>(() => persisted.source || null);
  const [selectedSources, setSelectedSources] = useState<string[]>(() => {
    if (Array.isArray(persisted.selectedSources) && persisted.selectedSources.length) {
      return persisted.selectedSources.map(String).filter(Boolean);
    }
    if (persisted.source?.label) return [String(persisted.source.label)];
    if (typeof persisted.source === "string" && persisted.source.trim()) {
      return [persisted.source.trim()];
    }
    return [];
  });
  const [formLead, setFormLead] = useState<any>(
    () => persisted.formLead || []
  );
  const [selectedDateRange, setSelectedDateRange] = useState<YhDateRangeValue>(
    () =>
      (persisted.selectedDateRange as YhDateRangeValue) || {
        from: null,
        to: null,
      }
  );
  const [assignDateRange, setAssignDateRange] = useState<YhDateRangeValue>({
    from: null,
    to: null,
  });
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const editPopoverRef = useRef<HTMLDivElement>(null);
  const [type, setType] = useState("");
  const [allSatgedata, setAllStagedata] = useState<any>([]);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [selectedRow, setSelectedRow] = useState<null | any>(null);
  const [hierarchyMap, setHierarchyMap] = useState<{ [key: string]: string }>(
    {}
  );
  const [reminders, setReminders] = useState<{ [key: string]: any }>({});
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);

  // Department / Team / Member (drawer cascade)
  const [departmentList, setDepartmentList] = useState<any[]>([]);
  const [teamFilterOptions, setTeamFilterOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [teamMemberFilterOptions, setTeamMemberFilterOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>(
    () => {
      if (Array.isArray(persisted.selectedDepartmentIds)) {
        return persisted.selectedDepartmentIds.map(String).filter(Boolean);
      }
      return [];
    }
  );
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>(() => {
    if (Array.isArray(persisted.selectedTeamIds) && persisted.selectedTeamIds.length) {
      return persisted.selectedTeamIds.map(String).filter(Boolean);
    }
    const legacy = persisted.selectedTeamFilter?._id;
    return legacy ? [String(legacy)] : [];
  });
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>(() => {
    const teams =
      Array.isArray(persisted.selectedTeamIds) && persisted.selectedTeamIds.length
        ? persisted.selectedTeamIds.map(String).filter(Boolean)
        : persisted.selectedTeamFilter?._id
          ? [String(persisted.selectedTeamFilter._id)]
          : [];
    if (!teams.length) return [];
    if (
      Array.isArray(persisted.selectedMemberIds) &&
      persisted.selectedMemberIds.length
    ) {
      return persisted.selectedMemberIds.map(String).filter(Boolean);
    }
    const legacy = persisted.selectedMemberFilter?._id;
    return legacy ? [String(legacy)] : [];
  });
  useEffect(() => {
    applyLicenseKey();
  }, []);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (editPopoverRef.current && !editPopoverRef.current.contains(target)) {
        setAnchorEl(null);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const catalogLoaded = useRef(false);
  const remindersStarted = useRef(false);
  const loadReminders = () => {
    if (remindersStarted.current) return;
    remindersStarted.current = true;
    getReminderData()
      .then((response5) => {
        const rows = response5?.data || [];
        setRemData(rows);
        const map: Record<string, any[]> = {};
        rows.forEach((r: any) => {
          const leadId = String(r?.leadId || "").trim();
          if (!leadId || leadId === "NA") return;
          if (!map[leadId]) map[leadId] = [];
          map[leadId].push(r);
        });
        setReminders(map);
      })
      .catch((error) => {
        remindersStarted.current = false;
        console.error("Error fetching reminders:", error);
      });
  };
  const loadLeadCatalogs = () => {
    if (catalogLoaded.current) return;
    catalogLoaded.current = true;
    getAllStage()
      .then((stageRes) => {
        setStageData((stageRes.data || []).map((e: any) => e.status_name).filter((n: any) => n !== "Initial"));
      })
      .catch(() => undefined);
    getAllSource()
      .then((sourceRes) => {
        setStageSourse((sourceRes.data || []).map((e: any) => e.source_name));
      })
      .catch(() => undefined);
    getAllHierarchyMeta()
      .then((hierarchyRes) => {
        setHierarchyMap((hierarchyRes.data || []).reduce((acc: any, e: any) => { acc[e._id] = e.hierarchyName; return acc; }, {}));
      })
      .catch(() => undefined);
    getStage()
      .then((stageNewRes) => setAllStagedata(stageNewRes.data || []))
      .catch(() => undefined);
    getAllForms()
      .then((formRes) => {
        setFormName(
          (formRes.data || [])
            .filter((item: any) => item.status === "1")
            .map((i: any) => ({ id: i._id, label: i.form_name }))
        );
      })
      .catch(() => undefined);
  };

  useEffect(() => {
    if (!filtersOpen) return;
    loadLeadCatalogs();
  }, [filtersOpen]);

  // Lead data: re-fetch only when filter/search/pagination changes
  useEffect(() => {
    const timer = window.setTimeout(() => {
      tableDataShowHandler();
    }, 40);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formLead,
    search,
    paginationModel,
    selectedDateRange,
    assignDateRange,
    selectedStages,
    selectedSources,
    selectedTeamIds,
    selectedMemberIds,
    selectedDepartmentIds,
    activeOrganizationId,
    attentionFilter,
    untouchedDays,
    freshFilter,
  ]);

  // Deep-link from Team Dashboard / Calling Report
  useEffect(() => {
    const state = location.state || {};
    const q = new URLSearchParams(
      location.search.startsWith("?") ? location.search.slice(1) : location.search
    );
    const csv = (raw?: string | null) =>
      String(raw || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    if (state.overdue === "1" || state.untouchedDays) {
      setAttentionFilter(true);
      if (state.untouchedDays) {
        setUntouchedDays(Number(state.untouchedDays) || 3);
      }
    }
    const fromReport = Boolean(
      state.fromCallingReport ||
        q.get("memberId") ||
        q.get("from") ||
        q.get("assignFrom") ||
        q.get("formId") ||
        q.get("stage")
    );
    const memberId = String(state.memberId || q.get("memberId") || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (memberId.length) {
      setSelectedMemberIds(memberId);
      const names = String(state.memberName || q.get("memberName") || "")
        .split(",")
        .map((s) => s.trim());
      setTeamMemberFilterOptions(
        memberId.map((id, i) => ({
          value: id,
          label: names[i] || id,
        }))
      );
    }
    if (!fromReport) return;

    const from = String(state.dateFrom || q.get("from") || "").trim();
    const to = String(state.dateTo || q.get("to") || from).trim();
    if (from) {
      setSelectedDateRange({ from, to: to || from });
      setFreshFilter(false);
    }
    const assignFrom = String(state.assignFrom || q.get("assignFrom") || "").trim();
    const assignTo = String(state.assignTo || q.get("assignTo") || assignFrom).trim();
    setAssignDateRange(
      assignFrom ? { from: assignFrom, to: assignTo || assignFrom } : { from: null, to: null }
    );
    const formIds = csv(state.formId || q.get("formId")).concat(
      Array.isArray(state.formIds) ? state.formIds.map(String) : []
    );
    if (formIds.length) setFormLead(Array.from(new Set(formIds)));
    const stages = csv(q.get("stage")).concat(
      Array.isArray(state.stages) ? state.stages.map(String) : []
    );
    if (stages.length) setSelectedStages(Array.from(new Set(stages)));
    const sources = csv(q.get("source")).concat(
      Array.isArray(state.sources) ? state.sources.map(String) : []
    );
    if (sources.length) setSelectedSources(Array.from(new Set(sources)));
    const teamIds = csv(q.get("teamId")).concat(
      Array.isArray(state.teamIds) ? state.teamIds.map(String) : []
    );
    if (teamIds.length) setSelectedTeamIds(Array.from(new Set(teamIds)));
    else if (memberId.length) setSelectedTeamIds([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setAttentionFilter(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Persist filters across lead-details navigation
  useEffect(() => {
    saveLeadListFilters(
      FILTER_KEYS.teamLeads,
      {
      search,
      searchInput,
      selectedStages,
      selectedSources,
      formLead,
      selectedDateRange,
      selectedDepartmentIds,
      selectedTeamIds,
      selectedMemberIds,
      freshFilter,
      attentionFilter,
      untouchedDays,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    } as PersistedTeamLeadFilters,
      String(store.getState().auth?.userId || "")
    );
  }, [
    search,
    searchInput,
    selectedStages,
    selectedSources,
    formLead,
    selectedDateRange,
    selectedDepartmentIds,
    selectedTeamIds,
    selectedMemberIds,
    freshFilter,
    attentionFilter,
    untouchedDays,
    paginationModel.page,
    paginationModel.pageSize,
  ]);

  // Keep page/pageSize in the URL so refresh / back from lead details stays put
  useEffect(() => {
    const next = withListPagination(
      location.pathname || urls.teamleads,
      paginationModel.page,
      paginationModel.pageSize,
      location.search
    );
    const current = `${location.pathname || ""}${location.search || ""}`;
    if (next !== current) {
      history.replace(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  // Debounce search — only update search state (and trigger refetch) after 400ms idle
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearch = (newValue: string) => {
    setSearchInput(newValue);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearch(newValue);
    }, 400);
  };

  const loadTeamsForDepartments = async (departmentIds: string[]) => {
    try {
      const res = await getTeamsForFilter(departmentIds);
      const flat: { label: string; value: string }[] = [];
      const seen = new Set<string>();
      const pushTeam = (id: string, name: string) => {
        if (!id || seen.has(id)) return;
        seen.add(id);
        flat.push({ value: id, label: name });
      };
      for (const t of res?.data || []) {
        const tid = String(t._id || "");
        pushTeam(tid, String(t.team_name || "").trim() || tid);
        for (const st of t.sub_teams || []) {
          const sid = String(st._id || "");
          const sname = String(st.team_name || "").trim();
          pushTeam(sid, sname ? `↳ ${sname}` : sid);
        }
      }
      setTeamFilterOptions(flat);
    } catch {
      setTeamFilterOptions([]);
    }
  };

  const loadMembersForTeams = async (teamIds: string[]) => {
    if (!teamIds.length) {
      return;
    }
    try {
      const res = await getMembersForFilter(teamIds);
      const opts: { label: string; value: string }[] = [];
      const seen = new Set<string>();
      for (const t of res?.data || []) {
        for (const m of t.team_members || []) {
          const user = Array.isArray(m.users) ? m.users[0] : m.users || m;
          const id = String(
            m.userId || user?._id || user?.userId || m._id || ""
          ).trim();
          const name = String(
            user?.name || user?.fullName || m.name || ""
          ).trim();
          if (!id || seen.has(id)) continue;
          seen.add(id);
          opts.push({ value: id, label: name || id });
        }
      }
      setTeamMemberFilterOptions(opts);
    } catch {
      setTeamMemberFilterOptions([]);
    }
  };

  // Load departments on mount
  useEffect(() => {
    const fetchDepts = async () => {
      try {
        const res = await getAllDepartmentsForFilter();
        setDepartmentList(res?.data || []);
      } catch { /* ignore */ }
    };
    fetchDepts();
  }, []);

  useEffect(() => {
    void loadTeamsForDepartments(selectedDepartmentIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartmentIds]);

  useEffect(() => {
    void loadMembersForTeams(selectedTeamIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeamIds]);

  // Restore form chip objects once form options are loaded
  useEffect(() => {
    if (!formLead?.length || !formName?.length) return;
    const selected = formName.filter((item: any) =>
      formLead.some((id: any) => String(id) === String(item.id))
    );
    if (selected.length) setTableData(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formName]);

  const flattenStageData = (

    data: any[]
  ): { label: string; isParent: boolean }[] => {
    const result: { label: string; isParent: boolean }[] = [];

    const extractStageNames = (stages: any[]) => {

      stages.forEach((stage) => {
        if (stage.stage_Name) {
          result.push({ label: stage.stage_Name, isParent: true }); // Parent entry
        }
        if (stage.sub_Stage_name && Array.isArray(stage.sub_Stage_name)) {
          stage.sub_Stage_name.forEach((subStage: any) => {
            result.push({ label: subStage.stage_Name, isParent: false }); // Child entry
          });
        }
      });
    };

    extractStageNames(data);
    return result;
  };

  const stageOptions = flattenStageData(Array.isArray(allSatgedata) ? allSatgedata : []);



  const handlePageSizeChange = (event: any) => {
    setPaginationModel({
      ...paginationModel,
      pageSize: event.target.value,
      page: 0,
    });
  };
  const filterLeadByForm = async (value: any[]) => {
    setFormLead(value);
  };


  const onClickHandler = () => {
    setUserId("");
    setNewTeamDialog({
      open: true,
    });
  };

  const closeHandler = () => {
    setNewTeamDialog(false);
    setCustomerDetails(false);
  };
  const handelDropdown = async (id: any) => {
    setLoading(true);
    try {
      setApiResponse(true);
      const response1 = await getAllTeamData(id?.id);
      const viewerTeamIds = await loadViewerTeamIds();
      const assignToData = filterByViewerTeams(
        (response1?.data || []).flatMap((team: any) => {
          const subTeams =
            team.sub_teams?.map((subTeam: any) => ({
              id: subTeam._id,
              label: subTeam.team_name,
              selected: subTeam?.is_available,
              isParent: false,
              teamMembers: subTeam.team_members?.length
                ? subTeam.team_members.map((member: any) => {
                    return {
                      id: member.userId,
                      label: member.users?.[0]?.name,
                      selected: 0,
                    };
                  })
                : null,
            })) || [];

          const mainTeam = {
            id: team._id,
            label: team.team_name,
            selected: team?.is_available,
            isParent: true,
            teamMembers: team.team_members?.length
              ? team.team_members.map((member: any) => {
                  return {
                    id: member.userId,
                    label: member.users?.[0]?.name,
                    selected: 0,
                  };
                })
              : null,
          };
          return [mainTeam, ...subTeams];
        }),
        viewerTeamIds
      );
      setLeadOption(assignToData);

      const selectedData = assignToData
        .filter((e: any) => {
          return e?.selected == 1;
        })
        .map((e: any) => {
          return {
            id: e.id,
            label: e.label,
            selected: e?.selected,
            teamMembers: Array.isArray(e.teamMembers)
              ? e.teamMembers.map((member: any) => ({
                id: member.id,
                label: member.label,
                selected: 0,
              }))
              : null,
          };
        });

      setSelectedOptions(selectedData);

      const ids = assignToData
        .filter((e: any) => e?.selected === 1)
        .map((e: any) => e.id);
      if (ids?.length) {
        handelDropdownAfterSelect(ids, id);
      } else {
        // No teams — still show Admin self in member list
        const auth = store.getState().auth || {};
        const isAdminUser = !!(
          auth.isAdmin ||
          auth.isSuperAdmin ||
          String(auth.hierarchyLevel) === "1"
        );
        if (isAdminUser && auth.userId) {
          const meId = String(auth.userId).replace(/['"]/g, "").trim();
          setLeadOptionMembersas([
            {
              pId: "",
              id: meId,
              label: auth.userName
                ? `${auth.userName} (Admin)`
                : "Admin (me)",
              selected: 0,
              isAdmin: true,
            },
          ]);
        }
      }
      setLoading(false);
    } catch {
      setLoading(false);
    } finally {
      setApiResponse(false);
      setLoading(false);
    }
  };

  const openCustomer = (params: any) => {
    const lead = params?.row || params;
    const leadId = lead?._id || params?._id;
    if (!leadId) return;
    try {
      sessionStorage.setItem(`all-lead-details-${leadId}`, JSON.stringify(lead));
      const navList = (userDataSource1 || [])
        .filter((item: any) => item?._id)
        .map((item: any) => ({ ...item }));
      sessionStorage.setItem("all-leads-nav-list", JSON.stringify(navList));
    } catch {
      // ignore storage failures
    }
    const returnPath = withListPagination(
      urls.teamleads,
      paginationModel.page,
      paginationModel.pageSize,
      location.search
    );
    setLeadListReturnPath(returnPath);
    history.push(urls.ALLLEADDETAILS.replace(":id", leadId), {
      lead,
      type: params?.type || "",
      from: returnPath,
    });
  };

  const handleEdit = (id: string) => {
    setNewTeamDialog({
      open: true,
      edit: true,
    });
    setUserId(id);
  };

  // const handleCloseLoan = () => {
  //   setIsDialogOpenLoan(false);
  // };

  // const handleCloseLoanList = () => {
  //   setIsDialogOpenLoanList(false);
  // };

  const handlePopOver = (
    event: React.MouseEvent<HTMLButtonElement>,
    user_id: string,
    status: any,
    userData: any
  ) => {
    setAnchorEl(event.currentTarget);
    setUserRow_Id(user_id);
    setRowStatus(status);
    setEventForEdit(userData);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleCloseModal = () => {
    setOpenModal({
      name: "",
      data: {},
      open: false,
    });
  };

  const getReminderInfo = async (id: any) => {
    try {
      setApiResponse(true);
      setLoading(true);
      const response = await getReminder(id);

      if (response.success == 1) {
        setResponseValuereminder((prevFormData: any) => ({
          ...prevFormData,
          value: response?.data,
        }));
      } else {
        setResponseValuereminder([]);
      }
      setLoading(false);
    } catch (error: any) {
      // openErrorNotification(
      //   isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      // );
      setLoading(false);
    } finally {
      setApiResponse(false);
      setLoading(false);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const handleRowClick = async (id: any) => {
    setShowSideBar(!showSideBar);
    setDynamicGridValue(id.row);
    setaddinationalValue(id?.row.dynamicFields || []);
    handelDropdown(id);
    getRemarkInfo(id?.id);
    getReminderInfo(id?.id);
  };

  const renderMenu = () => {
    if (!menuPosition) return null;
    return (
      <>
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setMenuPosition(null);
            setSelectedRow(null);
          }}
        />
        <div
          className="fixed z-50 w-[220px] max-w-full rounded-yh-md border border-yh-border bg-white py-1 shadow-yh"
          style={{
            top: menuPosition.top,
            left: Math.max(0, menuPosition.left - 190),
          }}
        >
          <div className="cursor-default px-3 py-2 text-xs font-yh-bold text-yh-light-gray">
            Actions
          </div>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-yh-black hover:bg-yh-lilac"
            onClick={() => {
              setNewTeamDialog({
                open: true,
                edit: true,
              });
              setEventForEdit(selectedRow);
              setUserId(selectedRow._id);
              setMenuPosition(null);
            }}
          >
            <EditSvg />
            Edit
          </button>
          {isNiOrLostStage(selectedRow?.stage) ? (
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-yh-black hover:bg-yh-lilac"
              onClick={() => {
                const row = selectedRow;
                setMenuPosition(null);
                if (!row?._id) return;
                setSelectedLeadIds([String(row._id)]);
                setSelectAllMatching(false);
                setBulkAssignOpen(true);
              }}
            >
              Reassign
            </button>
          ) : null}
        </div>
      </>
    );
  };

  const pageIds = useMemo(
    () =>
      (userDataSource1 || [])
        .map((r: any) => String(r?._id || ""))
        .filter(Boolean),
    [userDataSource1]
  );
  const matchingTotal = Number(metaData?.total || 0);
  const allPageSelected =
    pageIds.length > 0 &&
    pageIds.every((id: string) => selectedLeadIds.includes(id));
  const selectedCount = selectAllMatching
    ? matchingTotal || selectedLeadIds.length
    : selectedLeadIds.length;

  const clearLeadSelection = () => {
    setSelectAllMatching(false);
    setSelectedLeadIds([]);
  };

  const buildTeamLeadsFilterPayload = (pageNo: number, pageSize: number) => {
    const stages = expandStageSelection(selectedStages, allSatgedata);
    return {
      userId: store.getState().auth.userId,
      pageNo,
      pageSize,
      search: search || "",
      stage: stages.length ? stages.join(",") : "",
      source: selectedSources.length ? selectedSources.join(",") : "",
      formId: formLead.length > 0 ? formLead.join(",") : "",
      startDate:
        assignDateRange?.from || freshFilter || !selectedDateRange?.from
          ? ""
          : selectedDateRange.from,
      endDate:
        assignDateRange?.from || freshFilter || !selectedDateRange?.from
          ? ""
          : selectedDateRange.to || selectedDateRange.from,
      assignStartDate: assignDateRange?.from || "",
      assignEndDate: assignDateRange?.to || assignDateRange?.from || "",
      team_id: selectedMemberIds.length
        ? ""
        : selectedTeamIds.length
          ? selectedTeamIds.join(",")
          : "",
      memberId: selectedMemberIds.length ? selectedMemberIds.join(",") : "",
      overdue: attentionFilter ? "1" : "",
      untouchedDays: attentionFilter ? String(untouchedDays || 3) : "",
      fresh: freshFilter ? "1" : "",
    };
  };

  const toggleLeadSelect = (id: string, checked: boolean) => {
    setSelectAllMatching(false);
    setSelectedLeadIds((prev) => {
      if (checked) {
        return prev.includes(id) ? prev : [...prev, id];
      }
      return prev.filter((x) => x !== id);
    });
  };

  const toggleSelectAllPage = (checked: boolean) => {
    if (!checked) {
      clearLeadSelection();
      return;
    }
    setSelectAllMatching(false);
    setSelectedLeadIds((prev) => Array.from(new Set([...prev, ...pageIds])));
  };

  /** Select every team lead matching current filters across pages (API pageSize max 100). */
  const selectAllPagesMatching = async () => {
    if (!matchingTotal) {
      openErrorNotification("No leads match the current filters");
      return;
    }
    setSelectAllLoading(true);
    try {
      const pageSize = 100;
      const maxIds = 5000;
      const maxPages = Math.ceil(Math.min(matchingTotal, maxIds) / pageSize);
      const ids: string[] = [];
      for (let page = 0; page < maxPages; page++) {
        const res = await getAllTeamLeads(
          buildTeamLeadsFilterPayload(page, pageSize)
        );
        const batch = (Array.isArray(res?.data) ? res.data : [])
          .map((r: any) => String(r?._id || ""))
          .filter(Boolean);
        if (!batch.length) break;
        ids.push(...batch);
        if (ids.length >= matchingTotal || ids.length >= maxIds) break;
      }
      const unique = Array.from(new Set(ids));
      if (!unique.length) {
        openErrorNotification("No matching leads found");
        return;
      }
      const capped = unique.length < matchingTotal;
      setSelectAllMatching(!capped || unique.length >= matchingTotal);
      setSelectedLeadIds(unique);
      openSuccessNotification(
        capped && unique.length < matchingTotal
          ? `Selected first ${unique.length} of ${matchingTotal} matching leads`
          : `Selected all ${unique.length} matching lead(s) across pages`
      );
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to select all matching leads");
    } finally {
      setSelectAllLoading(false);
    }
  };

  const selectLeadsOnPage = () => {
    setSelectAllMatching(false);
    const ids = pageIds.slice();
    if (!ids.length) {
      openErrorNotification("No leads on this page");
      return;
    }
    setSelectedLeadIds((prev) => Array.from(new Set([...prev, ...ids])));
    openSuccessNotification(`Selected ${ids.length} lead(s) on this page`);
  };

  const columns: YhTableColumn[] = [
    {
      headerName: (
        <input
          type="checkbox"
          checked={selectAllMatching || allPageSelected}
          onChange={(e) => toggleSelectAllPage(e.target.checked)}
          aria-label="Select all on page"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      field: "_select",
      minWidth: 52,
      width: 52,
      renderCell: (params: YhTableCellParams) => {
        const id = String(params.row?._id || "");
        return (
          <input
            type="checkbox"
            className="action-button"
            data-action-button="true"
            checked={selectAllMatching || selectedLeadIds.includes(id)}
            onChange={(e) => {
              e.stopPropagation();
              toggleLeadSelect(id, e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            aria-label="Select lead"
          />
        );
      },
    },
    {
      headerName: "Lead",
      field: "leadName",
      minWidth: 220,
      renderCell: (params: YhTableCellParams) => {
        const name = params.row?.leadName || "?";
        const initial = String(name).trim().charAt(0).toUpperCase() || "?";
        const avatarPalette = [
          { bg: "#F9F0F4", color: "#6B1630" }, // purple
          { bg: "#DBEAFE", color: "#2563EB" }, // blue
          { bg: "#D1FAE5", color: "#059669" }, // green
          { bg: "#FFEDD5", color: "#EA580C" }, // orange
          { bg: "#FEF3C7", color: "#D97706" }, // amber
          { bg: "#FCE7F3", color: "#DB2777" }, // pink
          { bg: "#CCFBF1", color: "#0D9488" }, // teal
        ];
        let hash = 0;
        for (let i = 0; i < String(name).length; i++) {
          hash = String(name).charCodeAt(i) + ((hash << 5) - hash);
        }
        const avatar = avatarPalette[Math.abs(hash) % avatarPalette.length];

        return (
          <div
            className="action-button flex cursor-pointer items-center gap-3"
            data-action-button="true"
            onClick={(e) => {
              e.stopPropagation();
              openCustomer(params.row);
              setLeadData(params.row);
            }}
          >
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-sm font-bold"
              style={{ backgroundColor: avatar.bg, color: avatar.color }}
            >
              {initial}
            </span>
            <div className="flex min-w-0 flex-col items-start gap-0.5">
              <span className="truncate text-sm font-semibold capitalize text-[#A02448]">
                {name}
              </span>
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="shrink-0 text-xs text-[#9CA3AF]">
                  {params.row?.uid || "—"}
                </span>
                {(() => {
                  const arrival = leadActivityTime(params.row);
                  const age = relativeLeadTag(arrival);
                  const fresh = isFreshLeadToday(params.row);
                  if (!fresh && !age) return null;
                  return (
                    <>
                      {fresh ? (
                        <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                          Fresh
                        </span>
                      ) : null}
                      {age ? (
                        <span className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700">
                          {age}
                        </span>
                      ) : null}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      headerName: "Project name",
      field: "project_name",
      minWidth: 180,
      renderCell: (params: YhTableCellParams) => (
        <div className="flex min-w-0 flex-col items-start gap-0.5">
          <span className="truncate text-sm font-semibold capitalize text-[#111827]">
            {params.row.project_name || "—"}
          </span>
          <span className="truncate text-xs capitalize text-[#6B7280]">
            {params.row.developer_name || ""}
          </span>
        </div>
      ),
    },
    {
      headerName: "Reminder",
      field: "reminder",
      minWidth: 120,
      renderCell: (params: YhTableCellParams) => {
        const leadId = params.row._id;
        const reminderData = reminders[leadId];

        if (reminderData && reminderData.length > 0) {
          const latestReminder = reminderData.reduce(
            (
              latest: { date: any; time: any },
              current: { date: any; time: any }
            ) => {
              const latestDateTime = new Date(`${latest.date}T${latest.time}`);
              const currentDateTime = new Date(
                `${current.date}T${current.time}`
              );
              return currentDateTime > latestDateTime ? current : latest;
            }
          );

          const reminderDateTimeString = `${latestReminder.date}T${latestReminder.time}`;
          const reminderDateTime = new Date(reminderDateTimeString);
          const currentDate = new Date();
          const isExpired = reminderDateTime < currentDate;

          return (
            <div className="flex flex-col items-start gap-1">
              {!isExpired ? (
                <span className="inline-flex h-7 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 text-[11px] font-semibold text-emerald-600">
                  Reminder
                </span>
              ) : (
                <span className="inline-flex h-7 items-center justify-center rounded-full border border-red-200 bg-red-50 px-2.5 text-[11px] font-semibold text-red-600">
                  Expired
                </span>
              )}
            </div>
          );
        } else {
          return <div className="h-7 w-[70px]" />;
        }
      },
    },
    {
      headerName: "Source",
      field: "source",
      minWidth: 150,
      renderCell: (params: YhTableCellParams) => {
        const source = params.row?.source || "—";
        return (
          <div className="flex items-center gap-2.5">
            <YhSourceIcon source={source} />
            <span className="truncate text-sm font-medium capitalize text-[#374151]">
              {source}
            </span>
          </div>
        );
      },
    },
    {
      headerName: "Stage",
      field: "stage",
      minWidth: 130,
      renderCell: (params: YhTableCellParams) => (
        <button
          type="button"
          className="action-button inline-flex h-8 items-center rounded-full border border-orange-300 bg-orange-50 px-3 text-xs font-semibold capitalize text-orange-600 transition hover:bg-orange-100 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => {
            setOpenModal({
              name: "Stage Details",
              data: {
                stage: params?.row?.AssignToUserStage,
                members: params.row?.AssignToUser,
                leadStage: params.row?.stage || "Stages",
                leadName: params.row?.leadName,
                assignmentMode: params.row?.assignmentMode || "separate",
              },
              open: true,
            });
          }}
          data-action-button="true"
          disabled={
            !params?.row?.AssignToUserStage?.length && !params?.row?.stage
          }
        >
          {params.row?.stage || "Initial"}
        </button>
      ),
    },
    {
      headerName: "Assigned To",
      field: "at",
      minWidth: 150,
      renderCell: (params: YhTableCellParams) => {
        const teams = Array.isArray(params.row?.AssignTo)
          ? params.row.AssignTo
          : params.row?.AssignTo
            ? [params.row.AssignTo]
            : [];
        const members = Array.isArray(params.row?.AssignToUser)
          ? params.row.AssignToUser
          : params.row?.AssignToUser
            ? [params.row.AssignToUser]
            : [];
        const count = teams.length || members.length || 0;
        const shown = members.slice(0, 2);
        return (
          <button
            type="button"
            className="action-button flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => {
              setOpenModal({
                name: "Assigne-To",
                data: {
                  team: teams,
                  members,
                },
                open: true,
              });
            }}
            data-action-button="true"
            disabled={!count}
          >
            <span className="flex h-7 min-w-[28px] items-center justify-center rounded-full bg-[#F9F0F4] px-1.5 text-xs font-bold text-[#A02448]">
              {count}
            </span>
            <span className="flex -space-x-2">
              {shown.map((m: any, i: number) => {
                const label =
                  m?.fullName || m?.name || m?.userName || m?.email || "?";
                const ch = String(label).charAt(0).toUpperCase();
                return (
                  <span
                    key={m?._id || m?.id || i}
                    className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white text-[10px] font-bold text-white"
                    style={{
                      backgroundColor: ["#A02448", "#14B8A6", "#F59E0B"][i % 3],
                    }}
                    title={label}
                  >
                    {ch}
                  </span>
                );
              })}
            </span>
          </button>
        );
      },
    },
    {
      headerName: "Created Date",
      field: "createdAt",
      minWidth: 130,
      renderCell: (params) => {
        const parts = formatLeadDateParts(leadActivityTime(params.row));
        if (!parts) {
          return <span className="text-xs text-[#9CA3AF]">—</span>;
        }
        return (
          <div className="flex flex-col gap-0.5 text-xs leading-snug text-[#6B7280]">
            <div className="font-medium text-[#111827]">{parts.date}</div>
            <div>{parts.time}</div>
          </div>
        );
      },
    },
    {
      headerName: "Updated Date",
      field: "updatedAt",
      minWidth: 130,
      renderCell: (params) => {
        const parts = formatLeadDateParts(params.value, [
          params.row?.createdAt,
        ]);
        if (!parts) {
          return <span className="text-xs text-[#9CA3AF]">—</span>;
        }
        return (
          <div className="flex flex-col gap-0.5 text-xs leading-snug text-[#6B7280]">
            <div className="font-medium text-[#111827]">{parts.date}</div>
            <div>{parts.time}</div>
          </div>
        );
      },
    },
    {
      headerName: "Action",
      field: "action",
      minWidth: 80,
      renderCell: (params: YhTableCellParams) => (
        <button
          type="button"
          className="action-button flex h-8 w-8 items-center justify-center rounded-xl text-[#6B7280] transition-colors hover:bg-[#F3F4F6] hover:text-[#111827]"
          onClick={(event) => {
            event.stopPropagation();
            const rect = event.currentTarget.getBoundingClientRect();
            setMenuPosition({ top: rect.bottom, left: rect.left });
            loadLeadCatalogs();
            setSelectedRow(params.row);
          }}
          data-action-button="true"
        >
          <VerticalDotIcon />
        </button>
      ),
    },
  ];

  const handleAutocompleteChange = async (
    name: string,
    value: any,
    data: any,
    opts?: { reasonAlreadySaved?: boolean; reason?: string }
  ) => {
    if (!name) return;

    switch (name) {
      case "stage": {
        if (!value || String(value) === String(data?.stage || "")) break;
        if (isNiOrLostStage(value) && !opts?.reasonAlreadySaved) {
          setStageReason({ open: true, stage: value, lead: data });
          break;
        }
        const body: any = {
          id: data?._id,
          stage: value,
        };
        if (opts?.reason) {
          body.reason = opts.reason;
          body.stageReason = opts.reason;
        }
        try {
          setApiResponse(true);
          const response = await changeStage(body);
          if (response.success === 1) {
            const uid = String(store.getState().auth?.userId || "");
            const apply = (row: any) =>
              String(row?._id) === String(data?._id)
                ? patchLeadStageOnRow(row, value, uid, {
                    AssignToUser: response?.AssignToUser,
                  })
                : row;
            setUserDataSource1((prev: any[]) => (prev || []).map(apply));
            setSelectedRow((prev: any) => (prev ? apply(prev) : prev));
            setShowSideBar(showSideBar);
            openSuccessNotification(response?.message);
          } else {
            openErrorNotification(
              response?.message || "Failed to update stage"
            );
          }
          setApiResponse(false);
        } catch (error: any) {
          setApiResponse(false);
          openErrorNotification(error.message || notifiers.GENERIC_ERROR);
        }
        break;
      }

      case "assignTo": {
        const body = { id: data?._id, AssignTo: value?.id };
        try {
          setApiResponse(true);
          await changeAt(body);
          tableDataShowHandler();
        } catch (error: any) {
          setApiResponse(false);
          openErrorNotification(error.message || notifiers.GENERIC_ERROR);
        }
        break;
      }

      case "formName": {
        if (!value || value.label === "All") {
          tableDataShowHandler();
          return;
        }

        const filteredData = response.filter((e: any) => e.formId === value.id);

        const transformedData = filteredData.map((userData: any) => ({
          lead: (
            <>
              <span className="font-yh-bold text-xs text-yh-black">{userData.uid}</span>
              <span className="font-yh-bold text-xs text-yh-black">
                {userData.leadName}
              </span>
            </>
          ),
          // reminder: <>{getReminderInfo(userData._id)}</>,
          property: (
            <span className="text-[11px] text-yh-black">
              {userData.projecttypeId}
            </span>
          ),
          contact: (
            <span className="text-[11px] text-yh-black">
              {userData.leadEmail}, {userData.leadPhone}
            </span>
          ),
          formName: (
            <span className="text-[11px] text-yh-black">
              {userData.form_name}
            </span>
          ),
          source: (
            <span className="text-[11px] text-yh-black">
              {userData.source}
            </span>
          ),
          stage: (
            <select
              className="yh-input h-9 text-xs"
              value={userData.stage || ""}
              onChange={(event) =>
                handleAutocompleteChange("stage", event.target.value, userData)
              }
            >
              <option value="" disabled>
                Select stage
              </option>
              {stageData.map((s: string) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          ),
          at: (
            <>
              {(userData.AssignTo || [])?.map((item: any) => (
                <span key={item.team_id} className="block text-xs text-yh-black">{item.team_name}</span>
              ))}
              {(userData.AssignToUser || [])?.map((item: any) => (
                <span key={item.user_id} className="block text-xs text-yh-black">{item.name}</span>
              ))}
            </>
          ),
          action: (
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-yh-md text-yh-black hover:bg-yh-lilac"
              onClick={(event) =>
                handlePopOver(
                  event,
                  userData.formId,
                  userData.status,
                  userData
                )
              }
            >
              <VerticalDotIcon />
            </button>
          ),
        }));
        setUserDataSource(transformedData);
        setUserDataSource1(filteredData);

        setApiResponse(false);
        setLoading(false);
        break;
      }

      default:
        console.warn("Unhandled case in handleAutocompleteChange");
    }
  };
  const handelSideSave = async (body: any) => {
    try {
      setApiResponse(true);
      const response = await saveReminder(body);
      if (response.success === 1) {
        const [bulkRes, oneRes] = await Promise.all([
          getReminderData(),
          getReminder(body.leadId),
        ]);
        const rows = bulkRes?.data || [];
        setRemData(rows);
        const map: Record<string, any[]> = {};
        rows.forEach((r: any) => {
          const leadId = String(r?.leadId || "").trim();
          if (!leadId || leadId === "NA") return;
          if (!map[leadId]) map[leadId] = [];
          map[leadId].push(r);
        });
        if (oneRes?.data) {
          map[String(body.leadId)] = Array.isArray(oneRes.data)
            ? oneRes.data
            : [oneRes.data];
        }
        setReminders(map);
        tableDataShowHandler();
      }
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setApiResponse(false);
    }
  };
  const handelAssignToUser = async (dynamicGridValue: any) => {
    try {
      if (selectedOptionsMembers?.length) {

        const idMembers = selectedOptionsMembers.map((item: any) => item.id);
        const body = {
          ["id"]: dynamicGridValue?._id,
          ["AssignToUser"]: idMembers.join(","),
        };
        const response = await assignToMembers(body);
        setIdForMember([]);
        setIdForTeam([]);

        tableDataShowHandler();
      } else {
        const idTeam = selectedOptions.map((item: any) => {
          return item.id;
        });
        const idTeamVal = idTeam.reduce(
          (val: any, item: any) => val + "," + `'${item}'`
        );
        const body1 = {
          ["id"]: dynamicGridValue.row?._id,
          ["AssignTo"]: idTeamVal,
        };
        const response2 = await changeAt(body1);
        setIdForMember([]);
        setIdForTeam([]);
        setSelectedOptionsMembers([]);
        setSelectedOptions([]);
        setLeadOptionMembersas([]);
        tableDataShowHandler();
      }
    } catch (error: any) { }
  };

  const handleAssignToMe = async (dynamicGridValue: any) => {
    try {
      const auth = store.getState().auth || {};
      if (!canAssignToSelf(auth)) {
        openErrorNotification(
          "Only Admin, Super Admin, or Team Lead can assign to self"
        );
        return;
      }
      const meId = String(auth.userId || "")
        .replace(/['"]/g, "")
        .trim();
      if (!meId) {
        openErrorNotification("Not signed in");
        return;
      }
      const leadId = dynamicGridValue?._id || dynamicGridValue?.row?._id;
      if (!leadId) {
        openErrorNotification("Lead not found");
        return;
      }
      const response = await assignToMembers({
        id: leadId,
        AssignToUser: meId,
      });
      if (!(response?.success === 1 || response?.success === true)) {
        openErrorNotification(response?.message || "Failed to assign to you");
        return;
      }
      openSuccessNotification("Lead assigned to you");
      tableDataShowHandler();
    } catch (error: any) {
      openErrorNotification(error?.message || "Failed to assign to you");
    }
  };

  const tableDataShowHandler = async (opts?: { silent?: boolean }) => {
    const silent = !!opts?.silent;
    try {
      if (!silent) {
        setApiResponse(true);
        setLoading(true);
      }

      const payload = {
        userId: store.getState().auth.userId,
        pageNo: paginationModel.page,
        pageSize: paginationModel.pageSize,
        search: search || "",
        stage: (() => {
          const stages = expandStageSelection(selectedStages, allSatgedata);
          return stages.length ? stages.join(",") : "";
        })(),
        source: selectedSources.length ? selectedSources.join(",") : "",
        formId: formLead.length > 0 ? formLead.join(",") : "",
        startDate:
          assignDateRange?.from || freshFilter || !selectedDateRange?.from
            ? ""
            : selectedDateRange.from,
        endDate:
          assignDateRange?.from || freshFilter || !selectedDateRange?.from
            ? ""
            : selectedDateRange.to || selectedDateRange.from,
        assignStartDate: assignDateRange?.from || "",
        assignEndDate: assignDateRange?.to || assignDateRange?.from || "",
        team_id: selectedMemberIds.length
          ? ""
          : selectedTeamIds.length
            ? selectedTeamIds.join(",")
            : "",
        memberId: selectedMemberIds.length
          ? selectedMemberIds.join(",")
          : "",
        overdue: attentionFilter ? "1" : "",
        untouchedDays: attentionFilter ? String(untouchedDays || 3) : "",
        fresh: freshFilter ? "1" : "",
      };
      const response = await getAllTeamLeads(payload);
      let filteredData = Array.isArray(response?.data) ? response.data : [];
      setResponse(filteredData);
      setMetaData(
        response?.metadata?.[0]
          ? { ...response.metadata[0], total: response.metadata[0].total ?? 0 }
          : { total: 0, page: 0, stageCounts: {} }
      );
      if (!silent) setLoading(false);
      if (!filteredData.length) {
        setUserDataSource([]);
        setUserDataSource1([]);
        if (!silent) {
          setApiResponse(false);
        }
        return;
      }
      const tabelData = filteredData.map((userData: any, index: any) => {
        return {
          lead: (
            <>
              <span className="block font-yh-bold text-xs text-yh-black">
                {userData?.leadName}
              </span>
              <span className="block font-yh-bold text-xs text-yh-black">{userData?.uid}</span>
            </>
          ),
          property: (
            <>
              <span className="text-[11px] text-yh-black">
                {userData.projecttypeId}
              </span>
            </>
          ),
          contact: (
            <span className="text-[11px] text-yh-black">
              {userData.leadEmail},{userData.leadPhone} <br />{" "}
              {userData.leadPhone}{" "}
            </span>
          ),
          formName: (
            <span className="text-[11px] text-yh-black">
              {userData?.form_name}
            </span>
          ),
          source: (
            <>
              <span className="text-[11px] text-yh-black">
                {userData?.source}
              </span>
            </>
          ),
          stage: <>{userData.AssignToUserStage}</>,
          at: (
            <>
              {(userData?.AssignTo || [])?.length &&
                (userData?.AssignTo || [])?.map((item: any) => {
                  <span key={item.team_id}>{item.team_name}</span>;
                })}
            </>
          ),
          action: (
            <>
              <button
                type="button"
                className="flex h-8 w-8 items-center justify-center rounded-yh-md text-yh-black hover:bg-yh-lilac"
                onClick={(event: any) =>
                  handlePopOver(
                    event,
                    userData.formId,
                    userData.status,
                    userData
                  )
                }
              >
                <VerticalDotIcon />
              </button>
            </>
          ),
        };
      });
      const arr = new Array(20);
      setUserDataSource([...tabelData]);
      const filteredLeadData = filteredData
        .filter((data: any) => {
          const s = data?.status;
          return (
            s === undefined ||
            s === null ||
            s === "" ||
            String(s) === "1" ||
            s === 1
          );
        })
        .sort(sortLeadsByAssigned);

      setUserDataSource1([...filteredLeadData]);
      loadReminders();

      if (!silent) {
        setApiResponse(false);
        setLoading(false);
      }
    } catch (error: any) {
      if (!silent) {
        setUserDataSource([]);
        setUserDataSource1([]);
        setMetaData({ total: 0, page: 0, stageCounts: {} });
        setApiResponse(false);
        setLoading(false);
        openErrorNotification(
          isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
        );
      }
    }
  };

  useLeadRealtimeRefresh({
    userId: store.getState().auth.userId,
    showToast: true,
    onSilentRefresh: () => {
      void tableDataShowHandler({ silent: true });
    },
  });

  const handleChangeRadio = (event: any) => {
    setReminderStatus(event.target.value || event.target.defaultValue);
  };
  const handleCloseAddTeam = () => {
    setNewTeamDialog(false);
    tableDataShowHandler();
  };


  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedUser({ ...selectedUser, status: +event.target.value });
  };

  const dialogHeaderContent = () => {
    return (
      <div className="mr-3 mt-1 cursor-pointer">
        <CloseIcon onClick={() => setIsDialogOpen(false)} />
      </div>
    );
  };

  const dialogTitleContent = () => {
    return (
      <div className="px-[70px]">
        <div>
          <h2 className="font-yh-bold text-lg text-yh-black">Account Status</h2>
          <p className="mt-1 text-xs text-yh-light-gray">
            Change your account status
          </p>
        </div>

        <div className="mt-5">
          <p className="font-yh-bold text-sm text-yh-black">Current Status</p>

          <div className="mt-2.5 flex items-center gap-4">
            <CustomRadioButton
              value={1}
              checked={selectedUser.status == 1}
              onChange={handleRadioChange}
              label="Active"
            />

            <CustomRadioButton
              value={0}
              checked={selectedUser.status == 0}
              onChange={handleRadioChange}
              label="Deactive"
            />
          </div>
          <div>
            <CustomButton
              onClick={() => { }}
              label="Delete Account"
              buttonType="iconBtn"
              customClasses={{ color: "red" }}
            />
          </div>
        </div>
        <div className="flex w-[350px] justify-start gap-2.5">
          <CustomButton
            label="Save"
            onClick={() => { }}
            buttonType="primaryBtn"
          />
          <CustomButton
            label="Cancel"
            onClick={() => {
              setIsDialogOpen(false);
            }}
            buttonType="secondaryBtn"
          />
        </div>
      </div>
    );
  };

  const handelDropdownAfterSelect = async (ids: any, id: any = null) => {
    try {
      const payload = {
        team_id: ids,
        lead_id: id?.id,
      };
      const response1 = await getAllTeamMembersData(payload);
      const data =
        response1.data.flatMap((member: any) => {
          return (member?.team_members || []).map((mem: any) => {
            if (!mem.users || !mem.users[0]) return null;
            return {
              pId: member._id,
              id: mem.users[0]._id,
              label: mem.users[0]?.name,
              selected: mem?.is_available,
            };
          }).filter(Boolean);
        })

      setLeadOptionMembersas(data);
      const members = response1.data
        .flatMap((member: any) => {
          return member?.team_members?.map((mem: any) => {
            return {
              pId: member._id,
              id: mem.users[0]?._id,
              label: mem.users[0]?.name,
              selected: mem?.is_available,
            };
          });
        })
        .filter((item: any) => item.selected === 1);

      // Surface Admin / Super Admin even without team membership
      const auth = store.getState().auth || {};
      const isAdminUser = !!(
        auth.isAdmin ||
        auth.isSuperAdmin ||
        String(auth.hierarchyLevel) === "1"
      );
      if (isAdminUser && auth.userId) {
        const meId = String(auth.userId).replace(/['"]/g, "").trim();
        const list = Array.isArray(data) ? [...data] : [];
        if (meId && !list.some((m: any) => String(m.id) === meId)) {
          list.unshift({
            pId: "",
            id: meId,
            label: auth.userName
              ? `${auth.userName} (Admin)`
              : "Admin (me)",
            selected: 0,
            isAdmin: true,
          });
          setLeadOptionMembersas(list);
        }
      }

      setSelectedOptionsMembers(members);
    } catch { }
  };
  const handleOptionCheckboxChange =
    (option: any, value: any) => (event: any, index: any) => {
      if (event.target.checked) {
        const ids = [option.id, ...selectedOptions.map((e: any) => e.id)];
        handelDropdownAfterSelect(ids);
        setIdForTeam((prev: any) => [...prev, option.id]);
        const opt = { ...option, ["selected"]: 1 };
        const objIndex = leadOption.findIndex(
          (item: any) => item.id === option.id
        );
        leadOption[objIndex] = opt;
        setLeadOption((prev: any) => [...leadOption]);
        if (option.teamMembers) {
          setLeadOptionMembersas((prev: any) => {
            return [
              ...prev,

              ...option.teamMembers.map((member: any) => {
                return {
                  pId: option.id,
                  id: member.id,
                  label: member.label,
                  selected: 0,
                };
              }),
            ];
          });
        }
        setSelectedOptions([...selectedOptions, opt]);
      } else {
        setSelectedOptions(
          selectedOptions.filter((item: any) => item.id !== option.id)
        );
        setLeadOptionMembersas(
          leadOptionMembers.filter((item: any) => item?.pId !== option.id)
        );
        const opt = { ...option, ["selected"]: 0 };
        const objIndex = leadOption.findIndex(
          (item: any) => item.id === option.id
        );
        leadOption[objIndex] = opt;
        setLeadOption((prev: any) => [...leadOption]);
        setIdForTeam(idForTeam.filter((item: any) => item !== option?.id));
      }
    };
  const handleOptionCheckboxChange1 =
    (option: any, value: any) => (event: any, index: any) => {
      if (event.target.checked) {
        setIdForMember((prev: any) => [...prev, option.id]);
        const opt = { ...option, ["selected"]: 1 };
        const objIndex = leadOptionMembers.findIndex(
          (item: any) => item.id === option.id
        );
        leadOptionMembers[objIndex] = opt;
        setLeadOptionMembersas((prev: any) => [...leadOptionMembers]);
        setSelectedOptionsMembers([...selectedOptionsMembers, opt]);
      } else {
        setSelectedOptionsMembers(
          selectedOptionsMembers.filter((item: any) => item.id !== option.id)
        );
        const opt = { ...option, ["selected"]: 0 };
        const objIndex = leadOptionMembers.findIndex(
          (item: any) => item.id === option.id
        );
        leadOptionMembers[objIndex] = opt;
        setLeadOptionMembersas((prev: any) => [...leadOptionMembers]);
        setIdForMember(idForMember.filter((item: any) => item !== option?.id));
      }
    };
  const handleAssignToMember = (option: any, isChecked: boolean) => {
    const updatedOption = { ...option, selected: isChecked ? 1 : 0 };

    // Update the leadOptionMembers state immutably
    const updatedLeadOptions = leadOptionMembers.map((item: any) =>
      item.id === option.id ? updatedOption : item
    );

    setLeadOptionMembersas(updatedLeadOptions);

    // Update selected options
    const updatedSelectedOptions = isChecked
      ? [...selectedOptionsMembers, updatedOption]
      : selectedOptionsMembers.filter((item: any) => item.id !== option.id);

    setSelectedOptionsMembers(updatedSelectedOptions);

    // Update the idForMember array
    const updatedIdForMember = isChecked
      ? [...idForMember, option.id]
      : idForMember.filter((id: any) => id !== option.id);

    setIdForMember(updatedIdForMember);
  };

  const handleDateTimeChange = (event: any) => {
    const selectedDateTime = event.target.value;
    setReminderData((prevFormDataFields: any) => ({
      ...prevFormDataFields,
      ["date"]: event.target.value.split("T")[0],
      ["time"]: event.target.value.split("T")[1],
    }));
  };


  const handleChange = (value: any) => {
    setPaginationModel({
      pageSize: value.target.value,
      page: 0,
    });
  };
  const getRemarkInfo = async (id: any) => {
    try {
      setApiResponse(true);
      const response = await getRemark(id);
      if (response.success == 1) {
        setResponseValueremark((prevFormData: any) => ({
          ...prevFormData,

          value: response?.data,
        }));
      } else {
        setResponseValueremark([]);
      }
    } catch (error: any) {
      // openErrorNotification(
      //   isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      // );
    } finally {
      setApiResponse(false);
    }
  };
  const handelSideSaveRemark = async (body: any) => {
    const payloade = {
      leadId: body.leadId,
      notes: body.notes_remark,
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString(),
      title: "",
    };
    try {
      setApiResponse(true);
      const response = await saveRemark(payloade);
      if (response.success === 1) {
        // setDynamicGridValue("");
        setReminderData((prevData: any) => ({
          ...prevData,
          notes_remark: "",
        }));
      }
      getRemarkInfo(body.leadId);
      tableDataShowHandler();
    } catch (error: any) {
      setApiResponse(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const handelRemoveReminder = async (item: any) => {
    try {
      setApiResponse(true);
      const response = await deleteReminder(item);
      if ((response.sucess = 1)) {
      }
      tableDataShowHandler();
    } catch (error: any) {
      setApiResponse(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const importLeadExcelFile = async () => {
    if (!canExportLeads) {
      openErrorNotification(
        "You do not have permission to export leads. Ask an admin to grant you export access."
      );
      return;
    }
    let objectUrl = "";
    try {
      setdownloadLoading(true);
      const payload = {
        startDate: selectedDateRange?.from ? selectedDateRange.from : "",
        endDate: selectedDateRange?.from
          ? selectedDateRange.to || selectedDateRange.from
          : "",
        formId: formLead.length > 0 ? formLead.join(",") : "",
        organizationId: store.getState().auth?.activeOrganizationId || "",
      };
      const resExcle = await getDownloadLeadsExcle(payload);
      if (resExcle?.success && resExcle.fileData && resExcle.filename) {
        const byteCharacters = atob(resExcle.fileData);
        const byteNumbers = new Array(byteCharacters.length)
          .fill(0)
          .map((_, i) => byteCharacters.charCodeAt(i));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = resExcle.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        openSuccessNotification(
          resExcle.count ? `Exported ${resExcle.count} leads` : "Leads exported"
        );
      } else {
        // makeCall resolves instead of throwing on HTTP errors and timeouts.
        openErrorNotification(
          resExcle?.message ||
            "Could not export leads. Please narrow the date range and try again."
        );
      }
    } catch (err: any) {
      openErrorNotification(
        isTruthy(err?.message) ? err.message : notifiers.GENERIC_ERROR
      );
    } finally {
      if (objectUrl) {
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      }
      setdownloadLoading(false);
    }
  };


  const outlineBtnClass =
    "inline-flex h-10 shrink-0 items-center gap-2 rounded-xl border border-[#E5E7EB] bg-white px-3.5 text-sm font-medium text-[#374151] transition hover:border-[#D4B8C0] hover:bg-[#F8FAFC]";

  const clearAllFilters = () => {
    setSource(null);
    setSelectedSources([]);
    setSelectedStages([]);
    setTableData([]);
    filterLeadByForm([]);
    setSelectedDateRange({ from: null, to: null });
    setAssignDateRange({ from: null, to: null });
    setSearch("");
    setSearchInput("");
    setSelectedDepartmentIds([]);
    setSelectedTeamIds([]);
    setSelectedMemberIds([]);
    setTeamFilterOptions([]);
    setTeamMemberFilterOptions([]);
    setFreshFilter(false);
    setAttentionFilter(false);
    setUntouchedDays(3);
    clearLeadListFilters(
      FILTER_KEYS.teamLeads,
      String(store.getState().auth?.userId || "")
    );
  };

  const applyDrawerFilters = async (next: LeadFilterValues) => {
    const selected = formName.filter((item: any) =>
      next.formIds.some((v) => String(v) === String(item.id))
    );
    setTableData(selected);
    filterLeadByForm(selected.map((item: any) => item.id));
    setSelectedSources(next.sources || []);
    setSource(next.sources?.[0] ? { label: next.sources[0] } : null);
    setSelectedStages(next.stages || []);
    const deptIds = next.departmentIds || [];
    let teamIds = next.teamIds || [];
    if (!teamIds.length && deptIds.length) {
      try {
        const res = await getTeamsForFilter(deptIds);
        const ids: string[] = [];
        const seen = new Set<string>();
        for (const t of res?.data || []) {
          const tid = String(t._id || "").trim();
          if (tid && !seen.has(tid)) {
            seen.add(tid);
            ids.push(tid);
          }
          for (const st of t.sub_teams || []) {
            const sid = String(st._id || "").trim();
            if (sid && !seen.has(sid)) {
              seen.add(sid);
              ids.push(sid);
            }
          }
        }
        teamIds = ids;
      } catch {
        teamIds = [];
      }
    }
    setSelectedDepartmentIds(deptIds);
    setSelectedTeamIds(teamIds);
    const memberIds =
      (next.teamIds || []).length > 0 ? next.memberIds || [] : [];
    setSelectedMemberIds(memberIds);
    setSelectedDateRange(next.dateRange || { from: null, to: null });
    setAssignDateRange({ from: null, to: null });
    setFreshFilter(Boolean(next.fresh));
    setAttentionFilter(Boolean(next.attention));
    setUntouchedDays(Number(next.untouchedDays) || 3);
    void loadTeamsForDepartments(deptIds);
    void loadMembersForTeams(next.teamIds || []);
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 }
    );
  };

  const activeFilterCount = useMemo(
    () =>
      countActiveLeadFilters(
        {
          formIds: tableData.map((t: any) => String(t.id)),
          sources: selectedSources,
          stages: selectedStages,
          assignment: "all",
          uploadLeadIds: [],
          memberIds: selectedMemberIds,
          departmentIds: selectedDepartmentIds,
          teamIds: selectedTeamIds,
          dateRange: selectedDateRange || { from: null, to: null },
          fresh: freshFilter,
          attention: attentionFilter,
        },
        {
          hideAssignment: true,
          hideBulkSet: true,
        }
      ),
    [
      tableData,
      selectedSources,
      selectedStages,
      selectedMemberIds,
      selectedDepartmentIds,
      selectedTeamIds,
      selectedDateRange,
      freshFilter,
      attentionFilter,
    ]
  );

  const drawerFilterValue = useMemo(
    (): LeadFilterValues => ({
      formIds: tableData.length
        ? tableData.map((t: any) => String(t.id))
        : (formLead || []).map(String),
      sources: selectedSources,
      stages: selectedStages,
      assignment: "all",
      uploadLeadIds: [],
      memberIds: selectedMemberIds,
      departmentIds: selectedDepartmentIds,
      teamIds: selectedTeamIds,
      dateRange: selectedDateRange || { from: null, to: null },
      fresh: freshFilter,
      attention: attentionFilter,
      untouchedDays,
    }),
    [
      tableData,
      formLead,
      selectedSources,
      selectedStages,
      selectedMemberIds,
      selectedDepartmentIds,
      selectedTeamIds,
      selectedDateRange,
      freshFilter,
      attentionFilter,
      untouchedDays,
    ]
  );

  const totalLeads = Number(metaData?.total || 0);
  /** Prefer filtered stageCounts from list API; fall back to current page rows. */
  const countByStageHint = (hints: string[]) => {
    const stageCounts = metaData?.stageCounts;
    if (stageCounts && typeof stageCounts === "object") {
      return Object.entries(stageCounts).reduce((sum, [stageName, count]) => {
        const s = String(stageName || "").toLowerCase();
        if (hints.some((h) => s.includes(h))) {
          return sum + (Number(count) || 0);
        }
        return sum;
      }, 0);
    }
    const pageRows = userDataSource1 || [];
    return pageRows.filter((row: any) => {
      const primary = String(row?.stage || "").toLowerCase();
      const fromAssign = Array.isArray(row?.AssignToUserStage)
        ? row.AssignToUserStage.map((x: any) =>
            typeof x === "string" ? x : x?.stage || ""
          )
            .join(" ")
            .toLowerCase()
        : String(row?.AssignToUserStage || "").toLowerCase();
      const stages = primary || fromAssign;
      return hints.some((h) => stages.includes(h));
    }).length;
  };

  const getShowAllPage = () => {
    return (
      <>
        <div className="flex h-full min-h-0 w-full flex-col">
          {/* Page header */}
          <div className="mb-4 flex shrink-0 flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold tracking-tight text-[#111827]">
                Team Leads
              </h3>
              <p className="mt-1 text-sm text-[#6B7280]">
                View &amp; manage your team leads here.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {canExportLeads ? (
                <button
                  type="button"
                  className={outlineBtnClass}
                  disabled={downloadLoading}
                  onClick={() => importLeadExcelFile()}
                  title="Export the currently filtered leads to Excel"
                >
                  <DownloadSvg />
                  {downloadLoading ? "Downloading…" : "Download"}
                </button>
              ) : null}

              <button
                type="button"
                className={outlineBtnClass}
                onClick={selectAllPagesMatching}
                disabled={selectAllLoading || !matchingTotal}
                title="Select every lead matching current filters (all pages)"
              >
                {selectAllLoading
                  ? "Selecting…"
                  : matchingTotal
                    ? `Select all ${matchingTotal}`
                    : "Select all"}
              </button>

              <button
                type="button"
                className={outlineBtnClass}
                onClick={selectLeadsOnPage}
                title="Select all leads on this page"
              >
                Select page
              </button>

              <button
                type="button"
                className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-xl border border-[#D4B8C0] bg-[#F9F0F4] px-4 text-sm font-semibold text-[#4338CA] transition hover:bg-[#F9F0F4] disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!selectedCount}
                onClick={() => setBulkAssignOpen(true)}
              >
                Re-assign ({selectedCount})
              </button>
            </div>
          </div>

          {/* KPI summary cards */}
          <div className="mb-4 grid shrink-0 grid-cols-2 gap-3 xl:grid-cols-5">
            <LeadStatCard
              title="Total Leads"
              value={totalLeads}
              accent="purple"
              icon={<UsersIcon size={20} />}
              loading={loading}
            />
            <LeadStatCard
              title="New Leads"
              value={countByStageHint(["new", "initial", "contact"])}
              accent="blue"
              icon={<UserAddIcon size={20} />}
              loading={loading}
            />
            <LeadStatCard
              title="In Progress"
              value={countByStageHint([
                "prospect",
                "opportunity",
                "follow",
                "progress",
                "lead",
              ])}
              accent="orange"
              icon={<ClockIcon size={20} />}
              loading={loading}
            />
            <LeadStatCard
              title="Converted"
              value={countByStageHint(["closure", "won", "convert", "booked"])}
              accent="green"
              icon={<CheckIconRi size={20} />}
              loading={loading}
            />
            <LeadStatCard
              title="Lost Leads"
              value={countByStageHint(["lost", "dead", "reject"])}
              accent="red"
              icon={<XCircleIcon size={20} />}
              loading={loading}
            />
          </div>

          {/* Search + Filters drawer */}
          <div
            id="team-leads-filters"
            className="mb-3 flex shrink-0 flex-col gap-2.5 desktop:flex-row desktop:flex-wrap desktop:items-center desktop:justify-between desktop:gap-3"
          >
            <div className="w-full min-w-0 desktop:max-w-[320px] desktop:flex-1 screen:max-w-[380px] wide:max-w-[420px]">
              <YhInput
                placeholder="Search by Id, Name, Email and Phone"
                value={searchInput}
                type="search"
                id="outlined-search"
                onChange={(e) => handleSearch(e.target.value)}
                autoFocus={isSearchFocused}
                startAdornment={<SearchSvg />}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                containerClassName="w-full"
              />
            </div>

            <div className="flex min-w-0 w-full flex-wrap items-center gap-2 desktop:w-auto desktop:justify-end">
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="inline-flex h-10 items-center gap-2 rounded-xl border border-[#E5E7EB] bg-white px-3.5 text-sm font-semibold text-[#374151] transition hover:border-[#D4B8C0] hover:bg-[#F8FAFC]"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
                </svg>
                Filters
                {activeFilterCount > 0 ? (
                  <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#A02448] px-1.5 text-[11px] font-bold text-white">
                    {activeFilterCount}
                  </span>
                ) : null}
              </button>
              {activeFilterCount > 0 || search ? (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="shrink-0 whitespace-nowrap px-2 text-sm font-semibold text-[#A02448] hover:underline"
                >
                  Clear All
                </button>
              ) : null}
            </div>
          </div>

          <LeadFiltersDrawer
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            title="Team Leads filters"
            value={drawerFilterValue}
            onApply={applyDrawerFilters}
            onClear={clearAllFilters}
            formOptions={formName.map((option: any) => ({
              label: option.label,
              value: String(option.id),
            }))}
            sourceOptions={(stageSourse || []).map((option: string) => ({
              label: option,
              value: option,
            }))}
            stageOptions={(stageOptions || [])
              .map((o: any) => String(o?.label || "").trim())
              .filter(Boolean)
              .filter(
                (label: string, i: number, arr: string[]) =>
                  arr.findIndex(
                    (x) => x.toLowerCase() === label.toLowerCase()
                  ) === i
              )
              .map((label: string) => ({ label, value: label }))}
            departmentOptions={departmentList.map((d: any) => ({
              label: String(d.department_name || d.name || "").trim(),
              value: String(d._id || ""),
            })).filter((o: any) => o.label && o.value)}
            teamOptions={teamFilterOptions}
            teamMemberOptions={teamMemberFilterOptions}
            memberSingleSelect
            hideAssignment
            hideBulkSet
            showFresh
            showAttention
            onDepartmentIdsChange={(ids) => {
              void loadTeamsForDepartments(ids);
            }}
            onTeamIdsChange={(ids) => {
              void loadMembersForTeams(ids);
            }}
          />

          <div className="relative min-h-0 flex-1">
            {allPageSelected &&
            matchingTotal > pageIds.length &&
            !selectAllMatching ? (
              <div className="mb-3 rounded-xl border border-[#DDD6FE] bg-[#F5F3FF] px-4 py-2.5 text-sm text-[#5B21B6]">
                All {pageIds.length} leads on this page are selected.{" "}
                <button
                  type="button"
                  className="font-semibold underline disabled:opacity-50"
                  disabled={selectAllLoading}
                  onClick={selectAllPagesMatching}
                >
                  {selectAllLoading
                    ? "Selecting…"
                    : `Select all ${matchingTotal} matching filters (all pages)`}
                </button>
              </div>
            ) : null}
            {selectAllMatching ? (
              <div className="mb-3 rounded-xl border border-[#A7F3D0] bg-[#ECFDF5] px-4 py-2.5 text-sm text-[#065F46]">
                All {selectedCount} leads matching current filters are selected
                (across pages).{" "}
                <button
                  type="button"
                  className="font-semibold underline"
                  onClick={clearLeadSelection}
                >
                  Clear
                </button>
              </div>
            ) : null}
            {privileges &&
              (privileges?.["Team Leads"]?.includes("view") ||
                privileges?.["Team Leads"]?.includes("full control")) ? (
              <>
                <YhTable
                  columns={columns}
                  rows={userDataSource1}
                  getRowId={(row) => row._id}
                  rowHeight={80}
                  loading={loading}
                  emptyMessage="No data found"
                  className="h-full min-h-[280px]"
                  onRowClick={(row, e) => {
                    const targetElement = e.target as HTMLElement;
                    if (
                      targetElement?.classList?.contains("action-button") ||
                      targetElement?.closest?.(".action-button") ||
                      targetElement?.closest?.("[data-action-button]")
                    ) {
                      return;
                    }
                    handleRowClick({ row, id: row._id });
                  }}
                  page={paginationModel.page}
                  pageSize={paginationModel.pageSize}
                  totalRows={metaData?.total || 0}
                  onPageChange={(p) => setPaginationModel({ ...paginationModel, page: p })}
                  onPageSizeChange={(s) => setPaginationModel({ pageSize: s, page: 0 })}
                />

                {renderMenu()}
              </>
            ) : (
              ""
            )}
            {anchorEl && (
              <>
                <div className="fixed inset-0 z-40" onClick={handleClose} />
                <div
                  ref={editPopoverRef}
                  className="fixed z-50 flex w-[140px] max-w-full flex-col items-start rounded-yh-md border border-yh-border bg-white p-1 shadow-yh"
                  style={{
                    top: anchorEl.getBoundingClientRect().bottom + 10,
                    left: Math.max(0, anchorEl.getBoundingClientRect().left - 20),
                  }}
                >
                  {privileges &&
                    (privileges?.["Team Leads"]?.includes("edit") ||
                      privileges?.["Team Leads"]?.includes("full control")) ? (
                    <CustomButton
                      label="Edit"
                      buttonType="iconBtn"
                      onClick={() => {
                        handleEdit(userRow_Id ?? "");
                      }}
                    />
                  ) : (
                    ""
                  )}
                </div>
              </>
            )}
          </div>
          {addNewTeamDialog?.open && ( // modal for add new new lead
            <AddNewTeam
              isDialogOpen={true}
              handleCloseAddTeam={handleCloseAddTeam}
              userId={userId}
              setUserId={setUserId}
              addNewTeamDialog={addNewTeamDialog}
              eventForEdit={eventForEdit}
            />
          )}

          {customerDetails?.open && (
            <AddNewTeam2
              isDialogOpen={true}
              handleCloseAddTeam={closeHandler}
              userId={userId}
              setUserId={setUserId}
              addNewTeamDialog={addNewTeamDialog}
              eventForEdit={eventForEdit}
              leadData={leadData}
              stageData={stageData}
              formName={formName}
              stageSourse={stageSourse}
              responseValuereminder={responseValuereminder}
              responseValueremark={responseValueremark}
              selectedOptions={selectedOptions}
              leadOption={leadOption}
              type={type}
              allSatgedata={allSatgedata}
              addinationalValue={addinationalValue}
            />
          )}
          {apiResponse && <CustomLoader apiResponse={false} />}
        </div>
        <CustomDialog
          fullScreen={true}
          isDialogOpen={isDialogOpen}
          dialogHeaderContent={dialogHeaderContent()}
          dialogTitleContent={dialogTitleContent()}
        />

        {/* {isDialogOpenLoan && (
          <AddLoan
            isDialogOpenLoan={true}
            handleCloseLoan={handleCloseLoan}
            userId={userId}
            status={status}
          />
        )}

        {isDialogOpenLoanList && (
          <LoanList
            isDialogOpenLoan={true}
            handleCloseLoan={handleCloseLoanList}
            userId={userId}
            status={status}
          />
        )} */}
      </>
    );
  };

  return (
    <>
      <BulkAssignModal
        open={bulkAssignOpen}
        onClose={() => setBulkAssignOpen(false)}
        onAssigned={() => {
          clearLeadSelection();
          tableDataShowHandler();
        }}
        leadIds={selectedLeadIds}
        sourceHint={selectedSources.join(", ")}
        defaultOrganizationId={String(activeOrganizationId || "")}
        leadOrganizationIds={(userDataSource1 || [])
          .filter((r: any) => selectedLeadIds.includes(String(r?._id || "")))
          .map((r: any) => String(r?.organizationId || ""))
          .filter(Boolean)}
      />
      <div className="flex h-full min-h-0 w-full gap-0 overflow-hidden">
        {/* Main table area — shrinks when drawer opens */}
        <div
          className={`h-full min-h-0 min-w-0 transition-[flex-basis,width,margin] duration-300 ease-in-out ${dynamicGridValue
              ? "w-full flex-[1_1_0%] pr-0 md:pr-3"
              : "w-full flex-[1_1_100%]"
            }`}
        >
          {getShowAllPage()}
        </div>

        {/* Side drawer — pushes in from the right */}
        <aside
          aria-hidden={!dynamicGridValue}
          className={`relative h-full shrink-0 overflow-hidden transition-[width,opacity] duration-300 ease-in-out ${dynamicGridValue
              ? "w-[280px] opacity-100 sm:w-[300px] xl:w-[340px]"
              : "pointer-events-none w-0 opacity-0"
            }`}
        >
          <div
            className={`relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.06)] transition-transform duration-300 ease-in-out ${dynamicGridValue ? "translate-x-0" : "translate-x-full"
              }`}
            style={{ width: "100%", minWidth: dynamicGridValue ? undefined : 0 }}
          >
            <button
              type="button"
              className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
              onClick={() => {
                setReminderStatus("");
                setDynamicGridValue("");
                setShowSideBar(false);
              }}
              aria-label="Close lead details"
            >
              <CloseIcon width={"12px"} height={"12px"} />
            </button>

            <div className="h-full w-full overflow-auto p-4 pt-12 [scrollbar-width:thin] [scroll-padding-bottom:96px] sm:min-w-[300px] xl:min-w-[340px]">
              {dynamicGridValue ? (
                <LeadDetailsComponent
                  dynamicGridValue={dynamicGridValue}
                  handleAutocompleteChange={handleAutocompleteChange}
                  handleOptionCheckboxChange={handleOptionCheckboxChange}
                  selectedOptions={selectedOptions}
                  leadOption={leadOption}
                  leadOptionMembers={leadOptionMembers}
                  handleOptionCheckboxChange1={handleOptionCheckboxChange1}
                  handleAssignToMember={handleAssignToMember}
                  selectedOptionsMembers={selectedOptionsMembers}
                  handleChangeRadio={handleChangeRadio}
                  reminderStatus={reminderStatus}
                  setReminderStatus={setReminderStatus}
                  setReminderData={setReminderData}
                  currentDateTime={currentDateTime}
                  handleDateTimeChange={handleDateTimeChange}
                  handelAssignToUser={handelAssignToUser}
                  handleAssignToMe={handleAssignToMe}
                  onAssigned={() => {
                    tableDataShowHandler();
                  }}
                  handelSideSave={handelSideSave}
                  reminderData={reminderData}
                  responseValuereminder={responseValuereminder}
                  addinationalValue={addinationalValue}
                  stageData={stageData}
                  isMyLead={false}
                  responseValueremark={responseValueremark}
                  handelSideSaveRemark={handelSideSaveRemark}
                  handelRemoveReminder={handelRemoveReminder}
                  onRemindersChange={(rows: any[]) => {
                    setResponseValuereminder({ value: rows });
                    const leadId = String(dynamicGridValue?._id || "");
                    if (leadId) {
                      setReminders((prev: any) => ({ ...prev, [leadId]: rows }));
                    }
                  }}
                  openCustomer={openCustomer}
                  setLeadData={setLeadData}
                  allSatgedata={allSatgedata}
                />
              ) : null}
            </div>
          </div>
        </aside>

        <YhModal
          open={openModal.open}
          onClose={handleCloseModal}
          width={720}
          title={
            openModal.name === "Stage Details" ? (
              <div className="flex items-start gap-3 pr-2">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F9F0F4] text-[#A02448]">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                    <rect x="9" y="3" width="6" height="4" rx="1" />
                    <path d="M9 12h6M9 16h4" />
                  </svg>
                </span>
                <div className="min-w-0">
                  <div className="text-lg font-bold leading-tight text-[#1E1B4B]">
                    Stage Details
                  </div>
                  <p className="mt-0.5 text-sm font-normal text-[#6B7280]">
                    Only people who changed this stage
                  </p>
                </div>
              </div>
            ) : openModal.name === "Assigne-To" ? (
              <div className="flex items-start gap-3 pr-2">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F9F0F4] text-[#A02448]">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                  </svg>
                </span>
                <div className="min-w-0">
                  <div className="text-lg font-bold leading-tight text-[#1E1B4B]">
                    Team List
                  </div>
                  <p className="mt-0.5 text-sm font-normal text-[#6B7280]">
                    View teams and their members
                  </p>
                </div>
              </div>
            ) : openModal.name === "Reminder" ? (
              "Reminder"
            ) : (
              "Team List"
            )
          }
          footer={
            openModal.name === "Stage Details" ||
              openModal.name === "Assigne-To" ? (
              <button
                type="button"
                onClick={handleCloseModal}
                className="inline-flex h-10 min-w-[96px] items-center justify-center rounded-xl border border-[#E5E7EB] bg-white px-5 text-sm font-semibold text-[#374151] transition hover:bg-[#F8FAFC]"
              >
                Close
              </button>
            ) : null
          }
        >
          {openModal.name === "Stage Details" ? (
            <StageDetailsPanel
              stages={openModal?.data?.stage}
              members={openModal?.data?.members}
              leadStage={openModal?.data?.leadStage}
              leadName={openModal?.data?.leadName}
              assignmentMode={openModal?.data?.assignmentMode || "separate"}
              hierarchyMap={hierarchyMap}
            />
          ) : openModal.name === "Reminder" ? (
            <div />
          ) : openModal.name === "Assigne-To" ? (
            (() => {
              const rawTeams = openModal?.data?.team;
              const teams = Array.isArray(rawTeams)
                ? rawTeams.filter(Boolean)
                : rawTeams && typeof rawTeams === "object"
                  ? [rawTeams]
                  : [];
              const rawMembers = openModal?.data?.members;
              const members = Array.isArray(rawMembers)
                ? rawMembers.filter(Boolean)
                : rawMembers && typeof rawMembers === "object"
                  ? [rawMembers]
                  : [];
              const teamIconColors = [
                { bg: "#F9F0F4", color: "#A02448" },
                { bg: "#DBEAFE", color: "#2563EB" },
                { bg: "#D1FAE5", color: "#059669" },
                { bg: "#FFEDD5", color: "#EA580C" },
              ];
              const memberPalette = [
                { bg: "#F9F0F4", color: "#6B1630" },
                { bg: "#DBEAFE", color: "#2563EB" },
                { bg: "#D1FAE5", color: "#059669" },
                { bg: "#FFEDD5", color: "#EA580C" },
                { bg: "#FCE7F3", color: "#DB2777" },
              ];

              const parseMemberTeamIds = (raw: any): string[] => {
                if (raw == null || raw === "" || raw === "NA") return [];
                try {
                  const arr =
                    typeof raw === "string"
                      ? raw.trim().startsWith("[")
                        ? JSON.parse(raw)
                        : raw.split(",")
                      : raw;
                  if (!Array.isArray(arr)) return [];
                  return arr
                    .map((id: any) => String(id ?? "").replace(/['"]/g, "").trim())
                    .filter(Boolean);
                } catch {
                  return [];
                }
              };

              const uniqueMembers = (() => {
                const seen = new Set<string>();
                return members.filter((m: any) => {
                  const id = String(m?._id ?? m?.id ?? m?.userId ?? "").trim();
                  if (!id || seen.has(id)) return false;
                  seen.add(id);
                  return true;
                });
              })();

              const assignedTeamIdSet = new Set(
                teams.map((t: any) => String(t?._id ?? t?.id ?? ""))
              );

              const membersForTeam = (teamId: string) => {
                const tid = String(teamId ?? "");
                const firstTeamId = String(teams[0]?._id ?? teams[0]?.id ?? "");
                const matched = uniqueMembers.filter((member: any) => {
                  const ids = parseMemberTeamIds(member.team_id);
                  return ids.some((id) => id === tid);
                });
                if (matched.length) return matched;
                // No office team on anyone — list all assignees under the first team
                if (
                  !uniqueMembers.some((m) => parseMemberTeamIds(m.team_id).length)
                ) {
                  return tid === firstTeamId ? uniqueMembers : [];
                }
                // Assignees whose office teams don't overlap this lead's teams
                const orphans = uniqueMembers.filter((member: any) => {
                  const ids = parseMemberTeamIds(member.team_id);
                  if (!ids.length) return true;
                  return !ids.some((id) => assignedTeamIdSet.has(id));
                });
                return tid === firstTeamId ? orphans : [];
              };

              const totalMembers = uniqueMembers.length;

              return (
                <div className="space-y-4">
                  {/* Stats banner */}
                  <div className="flex flex-wrap items-center gap-8 rounded-2xl border border-[#F9F0F4] bg-gradient-to-r from-[#F5F3FF] to-[#F9F0F4] px-5 py-4">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#A02448] shadow-sm">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                        </svg>
                      </span>
                      <div>
                        <p className="text-xs font-medium text-[#6B7280]">
                          Total Teams
                        </p>
                        <p className="text-base font-bold text-[#1E1B4B]">
                          {teams.length}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#A02448] shadow-sm">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </span>
                      <div>
                        <p className="text-xs font-medium text-[#6B7280]">
                          Total Members
                        </p>
                        <p className="text-base font-bold text-[#1E1B4B]">
                          {totalMembers}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Team rows */}
                  <div className="max-h-[340px] overflow-auto rounded-xl border border-[#EEF0F5]">
                    <div className="sticky top-0 z-[1] grid grid-cols-[1fr_1.4fr_24px] gap-3 border-b border-[#EEF0F5] bg-[#F8FAFC] px-4 py-3">
                      <span className="text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">
                        Team Name
                      </span>
                      <span className="text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">
                        Team Members
                      </span>
                      <span />
                    </div>

                    {teams.length === 0 && uniqueMembers.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-[#9CA3AF]">
                        No teams found
                      </div>
                    ) : teams.length === 0 ? (
                      <div className="grid grid-cols-[1fr_1.4fr_24px] items-center gap-3 border-t border-[#EEF0F5] px-4 py-3.5">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-[#111827]">
                            Direct assignees
                          </p>
                          <p className="text-xs text-[#9CA3AF]">
                            No team linked on this lead
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {uniqueMembers.map((member: any, mIdx: number) => {
                            const palette =
                              memberPalette[mIdx % memberPalette.length];
                            const name =
                              member?.name ||
                              member?.user_name ||
                              member?.email ||
                              "Member";
                            return (
                              <span
                                key={String(member?._id || member?.id || mIdx)}
                                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
                                style={{
                                  backgroundColor: palette.bg,
                                  color: palette.color,
                                }}
                              >
                                {name}
                              </span>
                            );
                          })}
                        </div>
                        <span />
                      </div>
                    ) : (
                      teams.map((item: any, index: number) => {
                        const teamMembers = membersForTeam(
                          String(item._id ?? item.id ?? "")
                        );
                        const icon =
                          teamIconColors[index % teamIconColors.length];
                        return (
                          <div
                            key={item._id || index}
                            className="grid grid-cols-[1fr_1.4fr_24px] items-center gap-3 border-t border-[#EEF0F5] px-4 py-3.5"
                          >
                            <div className="flex min-w-0 items-start gap-2.5">
                              <span
                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                                style={{
                                  backgroundColor: icon.bg,
                                  color: icon.color,
                                }}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                                  <circle cx="9" cy="7" r="4" />
                                  <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                                </svg>
                              </span>
                              <div className="min-w-0">
                                <p className="truncate text-sm font-bold text-[#111827]">
                                  {item.team_name || "—"}
                                </p>
                                <span className="mt-1 inline-flex rounded-full bg-[#F9F0F4] px-2 py-0.5 text-[11px] font-semibold text-[#A02448]">
                                  {teamMembers.length} Member
                                  {teamMembers.length === 1 ? "" : "s"}
                                </span>
                              </div>
                            </div>

                            <div className="flex min-w-0 flex-col gap-2">
                              {teamMembers.length === 0 ? (
                                <span className="text-xs text-[#9CA3AF]">
                                  No members
                                </span>
                              ) : (
                                teamMembers.map((member: any, idx: number) => {
                                  const name = member.name || "—";
                                  const designation =
                                    (member?.hierarchyId &&
                                      hierarchyMap[member.hierarchyId]) ||
                                    "";
                                  const shortRole = designation
                                    ? designation
                                      .split(" ")
                                      .map((w: string) => w[0])
                                      .join("")
                                      .slice(0, 10)
                                    : "";
                                  const initials =
                                    String(name)
                                      .split(" ")
                                      .filter(Boolean)
                                      .slice(0, 2)
                                      .map((p: string) => p[0]?.toUpperCase())
                                      .join("") || "?";
                                  const avatar =
                                    memberPalette[idx % memberPalette.length];
                                  return (
                                    <div
                                      key={member._id || idx}
                                      className="flex min-w-0 items-center gap-2"
                                    >
                                      <span
                                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
                                        style={{
                                          backgroundColor: avatar.bg,
                                          color: avatar.color,
                                        }}
                                      >
                                        {initials}
                                      </span>
                                      <span className="truncate text-sm font-semibold text-[#111827]">
                                        {name}
                                        {shortRole ? (
                                          <span className="font-normal text-[#9CA3AF]">
                                            {" "}
                                            ({shortRole})
                                          </span>
                                        ) : null}
                                      </span>
                                    </div>
                                  );
                                })
                              )}
                            </div>

                            <span className="flex justify-end text-[#9CA3AF]">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 18l6-6-6-6" />
                              </svg>
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })()
          ) : (
            <div />
          )}
        </YhModal>
      </div>

      <StageReasonModal
        open={stageReason.open}
        stageLabel={stageReason.stage}
        saving={savingStageReason}
        onCancel={() => setStageReason({ open: false, stage: "", lead: null })}
        onConfirm={async (reason) => {
          const lead = stageReason.lead;
          if (!lead?._id || !stageReason.stage) return;
          try {
            setSavingStageReason(true);
            await handleAutocompleteChange(
              "stage",
              stageReason.stage,
              lead,
              { reasonAlreadySaved: true, reason }
            );
            setStageReason({ open: false, stage: "", lead: null });
          } catch (error: any) {
            openErrorNotification(error?.message || notifiers.GENERIC_ERROR);
          } finally {
            setSavingStageReason(false);
          }
        }}
      />
    </>
  );
};

export default ShowAll;
